/** App display timezone — Israel (handles IST/IDT DST automatically). */
export const APP_TIMEZONE = "Asia/Jerusalem";

const LOCALE = "en-US";
const EMPTY_FALLBACK = "—";
const INVALID_FALLBACK = "Invalid date";

const HAS_TIMEZONE_SUFFIX = /(?:Z|[+-]\d{2}:\d{2})$/;

const defaultDateTimeOptions: Intl.DateTimeFormatOptions = {
  timeZone: APP_TIMEZONE,
};

const defaultDateOptions: Intl.DateTimeFormatOptions = {
  timeZone: APP_TIMEZONE,
  year: "numeric",
  month: "short",
  day: "numeric",
};

/** Naive API datetimes are UTC; offset/Z strings pass through unchanged. */
export function parseApiDateTime(iso: string): Date {
  if (!iso || typeof iso !== "string") {
    return new Date(NaN);
  }

  const trimmed = iso.trim();
  if (!trimmed) {
    return new Date(NaN);
  }

  const normalized = HAS_TIMEZONE_SUFFIX.test(trimmed)
    ? trimmed
    : `${trimmed}Z`;
  return new Date(normalized);
}

export function formatAppDateTime(
  iso: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = parseApiDateTime(iso);
  if (Number.isNaN(date.getTime())) {
    return EMPTY_FALLBACK;
  }

  return date.toLocaleString(LOCALE, {
    ...defaultDateTimeOptions,
    ...options,
    timeZone: APP_TIMEZONE,
  });
}

export function formatAppDate(
  iso: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = parseApiDateTime(iso);
  if (Number.isNaN(date.getTime())) {
    return EMPTY_FALLBACK;
  }

  return date.toLocaleDateString(LOCALE, {
    ...defaultDateOptions,
    ...options,
    timeZone: APP_TIMEZONE,
  });
}

/** Calendar date (YYYY-MM-DD) in the app timezone. */
export function getAppTimezoneYmd(date: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/** Whole-day difference between two instants in the app timezone. */
export function diffCalendarDaysInAppTz(from: Date, to: Date): number {
  const fromYmd = getAppTimezoneYmd(from);
  const toYmd = getAppTimezoneYmd(to);
  const fromMs = Date.parse(`${fromYmd}T12:00:00Z`);
  const toMs = Date.parse(`${toYmd}T12:00:00Z`);
  return Math.round((toMs - fromMs) / (1000 * 60 * 60 * 24));
}

export function formatRelativeTimeFromApi(iso: string): string {
  const date = parseApiDateTime(iso);
  if (Number.isNaN(date.getTime())) {
    return EMPTY_FALLBACK;
  }

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatAppDate(iso);
}

export const getDaysOverdue = (dueDate: Date, now: Date): number => {
  const differenceInTimeDueDate = now.getTime() - dueDate.getTime();
  return Math.floor(differenceInTimeDueDate / (1000 * 60 * 60 * 24));
};

export const getIsOverdue = (dueDate: Date, now: Date): boolean =>
  getDaysOverdue(dueDate, now) >= 1;

export { EMPTY_FALLBACK, INVALID_FALLBACK };
